import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useGetAllModulesQuery } from "../../../../graphql/generated.tsx";
import { useMutation, gql } from "@apollo/client";
import { MdDelete } from "react-icons/md";
import swal from "sweetalert";
import NetworkIndicator from "../../../Practice/Components/NetworkIndicator.jsx";
import Loader from "../../../../components/Loader";

const REQUEST_DELETION = gql`
  mutation RequestDeletion(
    $typeOfDeletion: String!
    $payload: JSON!
    $reasonForDeletion: String!
    $requestedBy: String!
    $title: String!
  ) {
    requestDeletion(
      typeOfDeletion: $typeOfDeletion
      payload: $payload
      reasonForDeletion: $reasonForDeletion
      requestedBy: $requestedBy
      title: $title
    ) {
      id
      statusOfApproval
    }
  }
`;

const DisplayModule = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const { data, loading, error, refetch } = useGetAllModulesQuery({
    fetchPolicy: "network-only",
  });
  const [requestDeletion] = useMutation(REQUEST_DELETION);

  useEffect(() => {
    setIsLoading(false);
  }, []);

  if (loading || isLoading) {
    return (
      <div className="flex items-center justify-center w-full min-h-screen">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center w-full min-h-screen text-red-600">
        Error: {error.message}
      </div>
    );
  }

  const modules = data?.getAllModules || [];

  const handleDelete = async (mod) => {
    try {
      const reason = await swal({
        text: `Enter reason for requesting deletion of module "${mod.name}":`,
        content: "input",
        buttons: { cancel: "Cancel", confirm: "Submit" },
      });

      if (!reason) return;

      const payload = {
        operationName: "DeleteModule",
        variables: { id: mod._id },
        query: `
          mutation DeleteModule($id: String!) {
            deleteModule(id: $id)
          }
        `,
      };

      const user = JSON.parse(localStorage.getItem("user"));
      const userdisplayName = user?.displayName || "Unknown User";

      const response = await requestDeletion({
        variables: {
          typeOfDeletion: "MODULE",
          payload,
          reasonForDeletion: reason,
          requestedBy: userdisplayName,
          title: mod.name,
        },
      });

      if (response?.data?.requestDeletion) {
        await swal("Request Submitted", "Your delete request has been sent for admin approval.", "success");
        await refetch();
      } else {
        await swal("Error ❌", "Failed to submit delete request. Please try again later.", "error");
      }
    } catch (err) {
      console.error(err);
      swal("Error", "Something went wrong while requesting deletion!", "error");
    }
  };

  return (
    <div className="relative w-full px-6 pb-20 mt-10 space-y-5">
      <NetworkIndicator />

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between mb-10">
        <h2 className="text-3xl font-extrabold text-blue-900">All Learning Tasks</h2>

        <button
          style={{ backgroundColor: "rgba(14,61,93,1)" }}
          className="px-4 py-2 font-semibold text-white transition-all rounded shadow hover:opacity-90"
          onClick={() => navigate("/admin/module/create")}
        >
          + Create Module
        </button>
      </div>

      {/* Module Cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {modules.length === 0 && (
          <div className="flex flex-col items-center justify-center p-10 text-center border border-blue-100 shadow-inner col-span-full bg-gradient-to-br from-blue-50 to-white rounded-2xl">
            <img
              src="https://cdn-icons-png.flaticon.com/512/4076/4076509.png"
              alt="empty"
              className="w-24 h-24 mb-4 opacity-80"
            />
            <h3 className="text-lg font-semibold text-gray-700">No modules found</h3>
            <p className="text-sm text-gray-500">Click “Create Module” to add your first learning module.</p>
          </div>
        )}

        {modules.map((mod) => {
          const numDays = mod.daywise_plan?.length || 0;
          const numQuestions = mod.daywise_plan?.reduce(
            (sum, d) => sum + (d.questions?.length || 0),
            0
          );

          return (
            <div
              key={mod._id}
              onClick={() => navigate(`/admin/module/create?id=${mod._id}`)}
              className="relative flex flex-col justify-between gap-4 p-6 overflow-hidden transition-transform duration-200 border border-blue-200 shadow-md cursor-pointer bg-gradient-to-br from-blue-50 via-white to-blue-100/40 rounded-2xl hover:-translate-y-1 hover:shadow-2xl group min-h-[160px]"
            >
              {/* ✅ Title + Delete */}
              <div className="flex items-start justify-between w-full gap-3">
                <span
                  className="text-lg font-extrabold text-blue-900 line-clamp-2"
                  title={mod.name}
                >
                  {mod.name}
                </span>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(mod);
                  }}
                  className="flex items-center justify-center p-1 text-red-600 bg-white rounded-full shadow hover:text-red-800 shrink-0"
                  title="Request Deletion"
                >
                  <MdDelete size={20} />
                </button>
              </div>

              {/* ✅ Questions & Days */}
              <div className="flex items-center justify-between mt-2 text-sm text-gray-600">
                <span>Total Questions: {numQuestions}</span>
                <span className="px-3 py-1 text-xs font-semibold text-blue-800 bg-blue-100 rounded-full shadow-sm whitespace-nowrap">
                  {numDays} Day{numDays !== 1 ? "s" : ""}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DisplayModule;
